import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useFormik } from 'formik';
import * as auth from '../../redux/AuthRedux';
import { login } from '../../redux/AuthCRUD';
import { toAbsoluteUrl } from '../../utils';
import { BASE_URL } from '../../../config/environmentConfig';
import { useTranslation } from 'react-i18next';
import { errorAlert } from '../../components/ToastMessage';
import { errorHandler } from '../../utils/newApplicationUtils';
import { VerificationCodeForm } from '../../components/profileSettings/forms/VerificationCodeForm';
import { IModalWindow, ModalWindow } from '../../components/modal/ModalWindow';
import { verifyLoginCodeAuthentication } from '../../store/identyti/actions/security';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { LinkComponent } from '../../components/LinkComponent';
import { useIdentity } from '../../store/identyti/identity-context';
import * as Sentry from '@sentry/react';
import { sendSentryMessage } from '../../utils/sentry-helper';
import { Buffer } from 'buffer';
import { removeFavoriteFiltersFromStorage } from '../../utils/filter-helpers';
import { BUTTON_TYPE, ButtonComponent } from '../../components/ButtonComponent';
import {
	APPLICATION_VIEW_BASE_VIEW,
	APPLICATION_VIEW_STEPS,
} from '../../components/application-view/ApplicationViewAsideMenu';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { merchantActionTypes } from '../../store/merchnts/action-types';
import { LoginLayout } from '../../components/login/LoginLayout';

import './Login.scss';
import {
	getLoginValidationSchema,
	LOGIN_FORM_FIELDS,
	LOGIN_FORM_FIELDS_NAMES,
	loginInitialValues,
} from '../../constants/login/login-form';
import { FormInput } from '../../components/merchants/FormInput';
import { InputType } from '../../components/form/form-input-constants';
import { ConfirmPasswordInput } from '../../components/comfirmPassword/ConfirmPasswordInput';

export interface IVerifyLoginCodeParams {
	email: string,
	session: string,
	code: string,
	rememberMFA: boolean
}

export enum STATUSES {
	DRAFT = 'DRAFT',
	CANCELLED = 'CANCELLED',
	SUBMITTED = 'SUBMITTED',
	IN_REVIEW = 'IN_REVIEW',
	APPROVED = 'APPROVED',
	DECLINED = 'DECLINED'
}

export enum STATUSES_CODES {
	DRAFT = 0,
	CANCELLED = 1,
	SUBMITTED = 2,
	IN_REVIEW = 3,
	APPROVED = 4,
	DECLINED = 5
}

export enum ADMIN_GROUPS {
	SUPER_ADMIN = 'SUPER_ADMIN',
	MERCHANT_ADMIN = 'MERCHANT_ADMIN',
	PARTNER_ADMIN = 'PARTNER_ADMIN',
	INTEGRATION_PARTNER_ADMIN = 'INTEGRATION_PARTNER_ADMIN',
}

export enum MERCHANT_GROUPS {
	MERCHANT_SUPPORT = 'MERCHANT_SUPPORT',
	MERCHANT_SUPPORT_ADMIN = 'MERCHANT_SUPPORT_ADMIN',
	MERCHANT_FINANCE = 'MERCHANT_FINANCE',
	MERCHANT_DEVELOPER = 'MERCHANT_DEVELOPER'
}

export const parseJwt = (token): any => {
	try {
		if (!token) return null;

		return JSON.parse(
			new Buffer(token.split('.')[1], 'base64').toString('binary')
		);
	} catch (e) {
		console.log('token error', e);
		return null;
	}
};

export const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export const executeRecaptcha = (): Promise<unknown> => {
	return new Promise((resolve, reject) => {
		// @ts-ignore
		window.grecaptcha?.ready(() => {
			// @ts-ignore
			window.grecaptcha
				.execute(SITE_KEY, { action: 'submit' })
				.then(token => {
					resolve(token);
				})
				.catch(e => {
					sendSentryMessage({ name: `Recaptcha error`, context: e });
					console.log('executeRecaptcha error', e);
					reject(e);
				});
		});
	});
};

export interface CodeModalProps {
	isOpen: boolean,
	session?: string,
	email?: string,
}

export function Login(): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const redirectUrl = localStorage.getItem('redirectUrl');
	const getUserEmail = localStorage.getItem('userEmail');
	const { dispatch: merchantDispatch } = useMerchants();
	const {
		identityState: { isVerificationLoginCodeLoading },
		dispatch: identityDispatch
	} = useIdentity();

	const [loading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [codeModalState, setCodeModalState] = useState<CodeModalProps>({
		isOpen: false,
		session: '',
		email: ''
	});
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));
	const closeCodeModal = (): void => setCodeModalState(prev => ({ ...prev, isOpen: false }));

	const showRecaptchaBadge = (): void => {
		const script = document.querySelector('.grecaptcha-badge');
		if (script) {
			// @ts-ignore
			script.style.visibility = 'visible';
		}
	};

	useEffect(() => {
		const script = document.createElement('script');
		script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
		document.body.appendChild(script);
		showRecaptchaBadge();

		// @ts-ignore
		// lottieRef?.current?.setSpeed(0.1);
	}, []);

	const hideRecaptcha = (): void => {
		const script = document.querySelector('.grecaptcha-badge');
		if (script) {
			// @ts-ignore
			script.style.visibility = 'hidden';
		}
	};

	const formik = useFormik({
		initialValues: loginInitialValues,
		validationSchema: getLoginValidationSchema(t),
		onSubmit: async (values) => {
			localStorage.removeItem('chosenMerchant');
			setLoading(true);
			setErrorMessage('');

			try {
				const recaptchaToken = await executeRecaptcha();

				const user = await login(values.email, values.password, recaptchaToken);

				if (user?.data?.daysToEnforceMFA) {
					localStorage.setItem('enforcedMFAData', JSON.stringify({
						daysLeft: user?.data?.daysToEnforceMFA,
						skipAvailable: user?.data?.skipAvailable,
					}));
				}

				if (user?.data?.session) {
					setCodeModalState({
						isOpen: true,
						session: user?.data?.session,
						email: values.email
					});
					setLoading(false);
				}

				if (user?.data?.id) {
					await handleSuccessLogin(user);
				}

			} catch (e) {
				handleErrorLogin(e);
			}
		}
	});

	const setRedirect = (parsedToken: any, userGroups?: string[]): void => {
		let url = '';
		if (userGroups?.includes(ADMIN_GROUPS.MERCHANT_ADMIN) && redirectUrl === '/platforms') {
			url = redirectUrl;
		} else if (!getUserEmail ||
			(getUserEmail && getUserEmail.toLowerCase() !== parsedToken.email.toLowerCase())) {
			localStorage.setItem('userEmail', parsedToken.email);
			url = '/dashboard';
			removeFavoriteFiltersFromStorage();
		} else {
			url = redirectUrl === '/platforms' ? '/dashboard' : redirectUrl;
		}
		hideRecaptcha();
		return navigate(url);
	};

	const handleSuccessLogin = async (user): Promise<void> => {
		try {
			setErrorMessage('');
			setLoading(true);

			// Add null check before accessing user data
			if (!user || !user.data) {
				console.error('User data is null or undefined');
				setLoading(false);
				setErrorMessage('Login failed: Invalid user data received');
				return;
			}

			const parsedToken = parseJwt(user.data.id);

			// Add null check for parsedToken
			if (!parsedToken) {
				console.error('Failed to parse token');
				setLoading(false);
				setErrorMessage('Login failed: Invalid token received');
				return;
			}

			const userGroups = parsedToken?.['cognito:groups'] || [];
			const applicantId = parsedToken?.['custom:mid'];
			const username = parsedToken?.['cognito:username'];
			const isPiiMasked = parsedToken?.['custom:isPiiMasked'];

			Sentry.setTag('mid', applicantId);
			Sentry.setTag('username', username);

			// TODO check partners permissions
			if (userGroups?.includes(ADMIN_GROUPS.SUPER_ADMIN) ||
				userGroups?.includes(ADMIN_GROUPS.PARTNER_ADMIN) ||
				userGroups?.includes(ADMIN_GROUPS.INTEGRATION_PARTNER_ADMIN)) {
				const loginData = {
					accessToken: user?.data?.accessToken,
					idToken: user?.data?.id,
					userData: {
						userId: parsedToken?.['cognito:username'],
						email: parsedToken?.email,
						emailVerified: parsedToken?.email_verified,
						userGroups,
						isPiiMasked,
						loginDate: new Date(),
					}
				};

				dispatch(auth.actions.login(loginData));

				setRedirect(parsedToken);

			} else if (userGroups?.includes(ADMIN_GROUPS.MERCHANT_ADMIN) && applicantId) {

				const STATUS_URL = `${BASE_URL.MERCHANTS}/onboarding/${applicantId}/status`;
				const result = await axios(STATUS_URL, { headers: { Authorization: `Bearer ${user.data.id}` }});
				const status = result?.data?.statusName;
				merchantDispatch({ type: merchantActionTypes.FETCH_STATUS_SUCCESS, payload: status });

				if (status === STATUSES.CANCELLED) {
					setLoading(false);
					return navigate('/auth/login');
				}

				const loginData = {
					accessToken: user?.data?.accessToken,
					idToken: user?.data?.id,
					userData: {
						userId: parsedToken?.['cognito:username'],
						email: parsedToken?.email,
						emailVerified: parsedToken?.email_verified,
						userGroups,
						applicationStatus: status,
						isPiiMasked,
						loginDate: new Date(),
					}
				};

				if (!status && status !== STATUSES.DRAFT) {
					throw new Error((!status && status !== STATUSES.DRAFT) ?
						'Either your login or password is wrong.' :
						'Something went wrong. Our team was informed. Please, try to login later');
				}

				setLoading(false);
				// setSubmitting(false);

				switch (status) {
					case STATUSES.DECLINED:
						return;
					case STATUSES.APPROVED:
						dispatch(auth.actions.login(loginData));

						setRedirect(parsedToken, userGroups);
						break;

					case STATUSES.DRAFT:
						localStorage.setItem('userEmail', parsedToken.email);
						dispatch(auth.actions.login(loginData));
						hideRecaptcha();
						return navigate(`/${APPLICATION_VIEW_BASE_VIEW}/${APPLICATION_VIEW_STEPS.BUSINESS_DETAILS}`);
					case STATUSES.CANCELLED:
					case STATUSES.SUBMITTED:
					case STATUSES.IN_REVIEW:
						localStorage.setItem('userEmail', parsedToken.email);
						dispatch(auth.actions.login(loginData));
						hideRecaptcha();
						return navigate(`/${APPLICATION_VIEW_BASE_VIEW}/${APPLICATION_VIEW_STEPS.SUMMARY}`);
				}
			} else if (userGroups?.includes(MERCHANT_GROUPS.MERCHANT_SUPPORT)
				|| userGroups?.includes(MERCHANT_GROUPS.MERCHANT_SUPPORT_ADMIN)
				|| userGroups?.includes(MERCHANT_GROUPS.MERCHANT_FINANCE)
				|| userGroups?.includes(MERCHANT_GROUPS.MERCHANT_DEVELOPER)) {
				const STATUS_URL = `${BASE_URL.IDENTITY}/users/status?username=${username}`;
				const result = await axios.post(STATUS_URL, null, { headers: { Authorization: `Bearer ${user.data.id}` }});

				const loginData = {
					accessToken: user?.data?.accessToken,
					idToken: user?.data?.id,
					userData: {
						userId: parsedToken?.['cognito:username'],
						email: parsedToken?.email,
						emailVerified: parsedToken?.email_verified,
						userGroups,
						isPiiMasked,
						// applicationStatus: result?.data
						loginDate: new Date(),
					}
				};

				setLoading(false);
				// setSubmitting(false);

				switch (result?.data) {
					case 'CONFIRMED':
						dispatch(auth.actions.login(loginData));

						setRedirect(parsedToken);
						break;

					case 'FORCE_CHANGE_PASSWORD':
						return navigate('/auth/forgot-password');
				}
			} else {
				// throw new Error('Either your login or password is wrong.');
				setErrorMessage('Something went wrong. Please, try to login later');
				setLoading(false);
			}

		} catch (e) {
			handleErrorLogin(e);
		}

	};

	const handleErrorLogin = (e): void => {
		setLoading(false);
		sendSentryMessage({ name: `Login error`, context: e });
		console.log('handleErrorLogin error', e.response);
		const loginError = Object.values(e.response?.data?.errors || {});
		// setSubmitting(false);
		if (e.response?.data?.['Captcha'] || e.response?.data?.errors?.General) {
			errorHandler({ e, formik });
			return;
		}
		if (!e?.response?.status && e.message) {
			setErrorMessage(e.message);
		} else if (loginError[0][0] === 'e_100' || loginError[0][0] === 'e_101') {
			setErrorMessage('Account is blocked. Please, contact administrator.');
		} else if (e?.response?.status === 403) {
			errorAlert({
				title: 'Something went wrong. Our team was informed. Please, try to login later',
				duration: 0,
				closable: true
			});
		} else {
			setErrorMessage('Either your login or password is wrong.');
		}
	};

	const handleConfirmCode = async (code: string, session: string, email: string, rememberMFA: boolean): Promise<void> => {
		const params: IVerifyLoginCodeParams = {
			email,
			session,
			code,
			rememberMFA
		};

		await verifyLoginCodeAuthentication(identityDispatch, params, handleErrorModal, closeCodeModal, handleSuccessLogin);
	};

	const handleErrorModal = (): void => {
		closeCodeModal();
		setModalState({
			isOpen: true,
			modalTitle: 'Use an authenticator app',
			content: (
				<div>
					<div className="fs-3 text-danger text-center mb-5">
						<FontAwesomeIcon icon={faTriangleExclamation} className="me-1"/>
						Incorrect verification code.
					</div>
					<div className="fs-6 px-15 text-center">
						Having trouble entering your code? Please, contact with {' '}
						<LinkComponent text="administrator." link='mailto:support@flex-charge.com'/>
					</div>

				</div>
			),
			confirmButtonText: t('PARTNERS.FAIL_BTN_NAME'),
			minHeight: 100,
			onConfirm: () => {
				closeModal();
				setCodeModalState(prev => ({ ...prev, isOpen: true }));
			},
			onClose: closeModal
		});
	};

	return (
		<>
			<LoginLayout>

				<div className="form-container">
					<a
						href={BASE_URL.LOGIN_URL}
						target="_blank"
						className="login-logo-link d-block text-center mb-10 w-100"
						rel="noreferrer"
					>
						<img
							className="cursor-pointer login-logo"
							src={toAbsoluteUrl('/media/flex-logo-small.svg')}
							alt="logo"
						/>
					</a>
					<form
						className="form w-100"
						onSubmit={formik.handleSubmit}
						noValidate
						id="kt_login_signin_form"
					>

						<h1 className="text-dark mb-3 text-center login-title">
							{t('LOGIN.TITLE')}
						</h1>

						<div className="d-flex align-items-center justify-content-center mb-5">
							<div className="text-gray-400 fw-bolder fs-4">
								{t('LOGIN.NEW')}{' '}
							</div>
							{
								process.env.REACT_APP_ENVIRONMENT === 'production'
									? <LinkComponent
											text={t('LOGIN.CREATE_ACCOUNT_LINK')}
											link={BASE_URL.CONTACTS_URL}
											additionalClass="ms-2 fs-4 text-dark fw-bolder"
											isUnderLine
										/>
									: <ButtonComponent
											buttonType={BUTTON_TYPE.FAKE_LINK}
											buttonName={t('LOGIN.CREATE_ACCOUNT_LINK')}
											onButtonClick={(): void => navigate('/auth/registration')}
											additionalClass="btn-sm no-letter-spacing text-dark fs-4 ms-2"
										/>
							}

						</div>

						{
							errorMessage
								? (
									<div className="mb-lg-5 alert alert-danger">
										<div className="alert-text font-weight-bold">{errorMessage}</div>
									</div>
								)
								: (
									<div className="mb-lg-5 alert alert-danger placeholder-block">
										<div className="alert-text font-weight-bold">
											{t('LOGIN.WRONG')}
										</div>
									</div>
								)

						}

						<FormInput
							formik={formik}
							fieldName={LOGIN_FORM_FIELDS.EMAIL}
							label={t(LOGIN_FORM_FIELDS_NAMES.EMAIL)}
							placeholder={t(LOGIN_FORM_FIELDS_NAMES.EMAIL)}
							inputType={InputType.Text}
							required={true}
							additionalClasses="col-sm-12 col-md-12"
						/>

						<div className="change-password-container position-relative">
							<ConfirmPasswordInput
								formik={formik}
								fieldName={LOGIN_FORM_FIELDS.PASSWORD}
								label={t(LOGIN_FORM_FIELDS_NAMES.PASSWORD)}
								placeholder={t(LOGIN_FORM_FIELDS_NAMES.PASSWORD)}
								showPassword={showPassword}
								setShowPassword={setShowPassword}
								additionalClass="password-container position-relative col-12 pb-10"
								autoComplete="password"
								isPasswordShown
								required
								isSmallInput
							/>

							<ButtonComponent
								buttonType={BUTTON_TYPE.FAKE_LINK}
								buttonName={t('LOGIN.FORGOT_PASSWORD_LINK')}
								onButtonClick={(): void => navigate('/auth/forgot-password')}
								additionalClass="btn-sm no-letter-spacing text-dark fs-6 login-forget-password position-absolute"
							/>

						</div>


						<ButtonComponent
							buttonType={BUTTON_TYPE.DEFAULT_BLACK}
							buttonName={t('LOGIN.SIGN_IN')}
							loading={loading}
							isDisabled={loading}
							type="submit"
							additionalClass="w-100 no-letter-spaсing mt-5"
						/>

					</form>
				</div>
			</LoginLayout>
			<div className="release-nodes">
				{BASE_URL.APP_VERSION}
			</div>

			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

			<ModalWindow
				isOpen={codeModalState.isOpen}
				modalTitle="Two-step authentication"
				backdrop="static"
				contentClassName='d-block'
				minHeight={260}
				onClose={closeCodeModal}
				content={
					<VerificationCodeForm
						onContinue={handleConfirmCode}
						isLoading={isVerificationLoginCodeLoading}
						session={codeModalState?.session}
						email={codeModalState?.email}
						showCheckbox
					/>
				}
			/>
		</>
	);
}
